// client/src/components/pages/Settings/LexiStarAssets/LexiStarAssets.jsx
//
// LexiStar asset register (ST-00188; T3-S01 register, T1-S08 D26 ownership).
// Settings > Monitoring > LexiStar Assets, route
// /settings/modules/monitoring/lexistar-assets.
//
// "You see a truck if your organisation owns it" (D26): the list is what
// GET /api/lexistar/assets returns for your organisation, each row badged with
// its access type (Transporter = you own it; Admin = a global admin looking at
// another organisation's asset).
//
// Permissions: page lexistar.assets.view (pagePermissions.js + ProtectedRoute,
// re-checked here); create / edit / delete / aliases / import
// lexistar.assets.manage; transfer lexistar.ownership.manage. Row actions also
// need the row to be yours (Transporter) or a global admin's view (Admin).
//
// Layout mirrors LocationOwnership.jsx (stat cards, one card with search and
// table, centred modals) under the shared PageHeader.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaTruck,
  FaSearch,
  FaEdit,
  FaTrash,
  FaExchangeAlt,
  FaFileImport,
  FaPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaTruckLoading,
} from 'react-icons/fa';
import PageHeader from '../../../common/PageHeader';
import Pagination from '../../../common/Pagination';
import { useAuth } from '../../../../context/AuthContext';
import {
  listAssets,
  createAsset,
  updateAsset,
  deleteAsset,
  importFromDevices,
} from '../../../../services/lexistarAssets';
import AssetForm, { ASSET_TYPE_LABELS } from './AssetForm';
import OwnershipTransferDialog from './OwnershipTransferDialog';
import AssetDrawer from './AssetDrawer';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ROAD_TYPES = ['horse', 'rigid', 'trailer', 'ldv'];
const YARD_TYPES = ['loader', 'plant'];

const ACCESS_STYLES = {
  Transporter: { label: 'Transporter', className: 'bg-primary-100 text-primary-800' },
  Owner: { label: 'Owner', className: 'bg-emerald-50 text-emerald-700' },
  Dispatcher: { label: 'Dispatcher', className: 'bg-blue-50 text-blue-700' },
  Receiver: { label: 'Receiver', className: 'bg-green-50 text-green-700' },
  Admin: { label: 'Admin view', className: 'bg-slate-100 text-slate-600' },
};

// Short labels for the table; the form and drawer use ASSET_TYPE_LABELS.
const TYPE_SHORT_LABELS = {
  horse: 'Horse',
  rigid: 'Rigid',
  trailer: 'Trailer',
  ldv: 'LDV',
  loader: 'Loader',
  plant: 'Plant',
};

const TYPE_STYLES = {
  horse: 'bg-blue-50 text-blue-700',
  rigid: 'bg-blue-50 text-blue-700',
  trailer: 'bg-purple-50 text-purple-700',
  ldv: 'bg-sky-50 text-sky-700',
  loader: 'bg-amber-50 text-amber-700',
  plant: 'bg-amber-50 text-amber-700',
};

/** Only the owner (Transporter) or a global admin (Admin view) may change a row. */
const isRowManageable = (asset) => ['Transporter', 'Admin'].includes(asset.access_type);

// --- Confirmation Dialog (as LocationOwnership) ---
const ConfirmDialog = ({ isOpen, title, message, confirmLabel, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 p-0 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow bg-red-500 hover:bg-red-600"
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Stat Card (as LocationOwnership) ---
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-surface rounded-xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="text-white" size={18} />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
    </div>
  </div>
);

const AccessBadge = ({ accessType }) => {
  const style = ACCESS_STYLES[accessType] || {
    label: accessType || '—',
    className: 'bg-slate-100 text-slate-600',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  );
};

const LexiStarAssets = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.assets.view');
  const canManage = hasPermission('lexistar.assets.manage');
  const canTransfer = hasPermission('lexistar.ownership.manage');

  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const [formModal, setFormModal] = useState({ open: false, asset: null });
  const [transferAsset, setTransferAsset] = useState(null);
  const [drawerAssetId, setDrawerAssetId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importing, setImporting] = useState(false);

  // Debounce the search box into the server-side ?q=.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setError(null);
      const res = await listAssets({ q: query, page, limit: PAGE_SIZE });
      setAssets(res.assets || []);
      setPagination(res.pagination || { page: 1, total: 0, totalPages: 1 });
      setByType(res.summary?.byType || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load assets.');
    } finally {
      setLoading(false);
    }
  }, [canView, query, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => {
    const sum = (types) => types.reduce((n, t) => n + (byType[t] || 0), 0);
    return {
      total: Object.values(byType).reduce((n, v) => n + v, 0),
      road: sum(ROAD_TYPES),
      yard: sum(YARD_TYPES),
    };
  }, [byType]);

  const handleSave = async (payload) => {
    if (formModal.asset) {
      await updateAsset(formModal.asset.id, payload);
      setNotice(`Saved ${payload.registration || formModal.asset.registration}.`);
    } else {
      const res = await createAsset(payload);
      setNotice(`Added ${res.asset?.registration || payload.registration}.`);
    }
    await fetchData();
  };

  const handleDelete = async () => {
    const asset = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteAsset(asset.id);
      setNotice(`Deleted ${asset.registration}.`);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete asset');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    try {
      const r = await importFromDevices();
      setNotice(
        `Imported ${r.created} asset${r.created === 1 ? '' : 's'} from the device registry. ` +
          `${r.alreadyRegistered} registration${r.alreadyRegistered === 1 ? '' : 's'} already had an asset.`
      );
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Import from the device registry failed');
    } finally {
      setImporting(false);
    }
  };

  const handleTransferred = async (result) => {
    const to = result?.to?.tenant_name || 'the new owner';
    setNotice(`Transferred ${result?.asset?.registration || 'the asset'} to ${to}.`);
    await fetchData();
  };

  const header = (
    <PageHeader
      icon={FaTruck}
      title="LexiStar Assets"
      subtitle="Trucks, trailers and loaders by owning organisation. You see an asset if your organisation owns it."
      actions={
        canView && canManage ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleImport}
              disabled={importing}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-60"
              title="Create one asset for every device-registry registration that has none yet"
            >
              <FaFileImport size={12} />
              <span className="hidden sm:inline">
                {importing ? 'Importing...' : 'Import from device registry'}
              </span>
            </button>
            <button
              onClick={() => setFormModal({ open: true, asset: null })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all shadow-sm"
            >
              <FaPlus size={12} /> New asset
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
            <p className="font-medium">You do not have permission to view LexiStar assets.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="ml-4 text-red-400 hover:text-red-600"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Notice */}
        {notice && (
          <div
            role="status"
            className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 flex items-start"
          >
            <FaCheckCircle className="mr-3 mt-0.5 flex-shrink-0 text-emerald-500" />
            <p className="flex-1 text-sm font-medium">{notice}</p>
            <button
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="ml-4 text-emerald-400 hover:text-emerald-600"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard label="Assets" value={stats.total} icon={FaTruck} color="bg-slate-600" />
          <StatCard label="Road fleet" value={stats.road} icon={FaTruck} color="bg-primary-500" />
          <StatCard
            label="Loaders & plant"
            value={stats.yard}
            icon={FaTruckLoading}
            color={stats.yard > 0 ? 'bg-amber-500' : 'bg-slate-400'}
          />
        </div>

        {/* Main Card */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-slate-600">
                {pagination.total} asset{pagination.total === 1 ? '' : 's'}
                {query ? ` matching "${query}"` : ''}
              </p>
              <div className="relative">
                <FaSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={14}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search registration or fleet no..."
                  aria-label="Search assets"
                  className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
                />
              </div>
            </div>
          </div>

          <div className="p-6">
            {loading && assets.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
                <p className="mt-4 text-slate-600">Loading assets...</p>
              </div>
            ) : assets.length === 0 ? (
              <div className="text-center py-12">
                <FaTruck className="mx-auto text-slate-300 mb-4" size={32} />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {query ? 'No matching assets' : 'No assets yet'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {query
                    ? 'Try a different registration or fleet number.'
                    : canManage
                      ? 'Add an asset, or import one per registration from the device registry.'
                      : 'Assets your organisation owns will appear here.'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Registration
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Fleet No
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Class
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Owned By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Access
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-slate-200">
                      {assets.map((a) => {
                        const manageable = isRowManageable(a);
                        return (
                          <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() => setDrawerAssetId(a.id)}
                                className="text-sm font-mono font-medium text-slate-900 hover:text-primary-600 hover:underline"
                                title="View aliases and ownership history"
                              >
                                {a.registration}
                              </button>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {a.fleet_no ? (
                                <span className="text-sm text-slate-700 font-mono">
                                  {a.fleet_no}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400 italic">—</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_STYLES[a.asset_type] || 'bg-slate-100 text-slate-700'}`}
                                title={ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}
                              >
                                {TYPE_SHORT_LABELS[a.asset_type] || a.asset_type}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              {a.vehicle_class ? (
                                <span className="text-sm text-slate-700">{a.vehicle_class}</span>
                              ) : (
                                <span className="text-xs text-slate-400 italic">—</span>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                {a.tenant_name
                                  ? `${a.tenant_name}${a.tenant_code ? ` (${a.tenant_code})` : ''}`
                                  : `Organisation ${a.tenant_id}`}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <AccessBadge accessType={a.access_type} />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              {canManage && manageable && (
                                <button
                                  onClick={() => setFormModal({ open: true, asset: a })}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all mr-1.5"
                                  aria-label={`Edit ${a.registration}`}
                                >
                                  <FaEdit size={11} /> Edit
                                </button>
                              )}
                              {canTransfer && manageable && (
                                <button
                                  onClick={() => setTransferAsset(a)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-all mr-1.5"
                                  aria-label={`Transfer ${a.registration}`}
                                >
                                  <FaExchangeAlt size={11} /> Transfer
                                </button>
                              )}
                              {canManage && manageable && (
                                <button
                                  onClick={() => setConfirmDelete(a)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all"
                                  aria-label={`Delete ${a.registration}`}
                                >
                                  <FaTrash size={11} /> Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  className="mt-6"
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={setPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssetForm
        isOpen={formModal.open}
        asset={formModal.asset}
        onClose={() => setFormModal({ open: false, asset: null })}
        onSave={handleSave}
      />

      <OwnershipTransferDialog
        isOpen={!!transferAsset}
        asset={transferAsset}
        onClose={() => setTransferAsset(null)}
        onTransferred={handleTransferred}
      />

      <AssetDrawer
        isOpen={!!drawerAssetId}
        assetId={drawerAssetId}
        canManage={canManage}
        onClose={() => setDrawerAssetId(null)}
        onChanged={fetchData}
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete asset"
        message={
          confirmDelete
            ? `Delete ${confirmDelete.registration}? It leaves the register and its aliases stop matching tickets. Its ownership history is kept.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default LexiStarAssets;
